import { type FunctionComponent, Fragment, useRef, useEffect } from "react"
import { Dialog, Popover, Transition } from "@headlessui/react"

import type { WithContentfulHeaderLocationFragment } from "~/contentful"
import { contentfulImageUrl } from "~/helpers/contentful"
import {
  getLocationCardHeadline,
  getLocationCardSubline,
  resolveLocationRouteSlug
} from "~/helpers/location"
import classNames from "classnames"
import Location from "~/icons/Location"
import { useNavigate } from "@remix-run/react"
import Close from "~/icons/Close"

const LocationButton: FunctionComponent<{
  location: WithContentfulHeaderLocationFragment
  handleClick: (locationPage: WithContentfulHeaderLocationFragment) => void
  selected?: boolean
}> = ({ location, handleClick, selected }) => {
  const headline = getLocationCardHeadline(location.name)
  const subline = getLocationCardSubline(location.name, location.city)

  return (
    <button
      className={classNames(
        "flex flex-col justify-center border rounded-lg h-full w-full",
        {
          "border-camp-primary": selected,
          "border-gray-100 p-4": !selected
        }
      )}
      onClick={() => handleClick(location)}
    >
      <div
        className={classNames(
          "items-center gap-2 text-camp-primary justify-center",
          {
            "flex flex-col": !selected,
            flex: selected
          }
        )}
      >
        {location.logo?.url && (
          <img
            loading="lazy"
            src={contentfulImageUrl(location.logo.url, {
              width: 280,
              height: 200
            })}
            alt=""
            className="min-w-16"
            width={140}
            height={100}
          />
        )}
        <div
          className={classNames({
            "text-left md:text-center": selected,
            "text-center": !selected
          })}
        >
          <div className="font-serif">{headline}</div>

          {location?.slug && subline && (
            <small
              className={classNames("text-center text-sm", {
                uppercase: subline.uppercase
              })}
            >
              {subline.text}
            </small>
          )}
        </div>
      </div>
    </button>
  )
}

export const LocationSelectPopover: FunctionComponent<{
  locationPages: WithContentfulHeaderLocationFragment[]
  closePopover: () => void
  onSelectLocation?: (locationName: string) => string | null
  selectedLocationName?: string
  isOpen?: boolean
  disableDialog?: boolean
}> = ({
  locationPages,
  closePopover,
  onSelectLocation,
  selectedLocationName,
  isOpen = false,
  disableDialog = false
}) => {
  const navigate = useNavigate()
  const mobileScrollRef = useRef<HTMLDivElement>(null)
  const desktopScrollRef = useRef<HTMLDivElement>(null)

  // Prevent scroll from bubbling to parent when scrolling popover
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const container = e.currentTarget as HTMLDivElement
      const canScrollUp = container.scrollTop > 0
      const canScrollDown =
        container.scrollTop < container.scrollHeight - container.clientHeight

      const isScrollingDown = e.deltaY > 0
      const isScrollingUp = e.deltaY < 0

      if (
        (isScrollingDown && !canScrollDown) ||
        (isScrollingUp && !canScrollUp)
      ) {
        e.preventDefault()
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      const container = e.currentTarget as HTMLDivElement
      if (container.scrollHeight > container.clientHeight) {
        // Has scrollable content, allow default scroll behavior
        return
      }
      e.preventDefault()
    }

    const refs = [mobileScrollRef, desktopScrollRef].filter(
      ref => ref.current && isOpen
    )

    refs.forEach(ref => {
      if (ref.current) {
        ref.current.addEventListener("wheel", handleWheel, { passive: false })
        ref.current.addEventListener("touchmove", handleTouchMove, {
          passive: false
        })
      }
    })

    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          ref.current.removeEventListener("wheel", handleWheel)
          ref.current.removeEventListener("touchmove", handleTouchMove)
        }
      })
    }
  }, [isOpen])

  const handleClick = (locationPage: WithContentfulHeaderLocationFragment) => {
    const destination =
      (onSelectLocation && locationPage.name
        ? onSelectLocation(locationPage.name)
        : null) ??
      (locationPage.slug
        ? `/${resolveLocationRouteSlug(locationPage.slug)}`
        : null)

    if (destination) navigate(destination)
    closePopover()
  }

  const selectedLocation = locationPages.find(
    location => location.name === selectedLocationName
  )

  const sortedLocationPages: WithContentfulHeaderLocationFragment[] =
    selectedLocation
      ? [
          selectedLocation,
          ...locationPages.filter(loc => loc !== selectedLocation)
        ]
      : locationPages

  const otherLocations = selectedLocation
    ? sortedLocationPages.slice(1)
    : sortedLocationPages

  const sheetContent = (
    <div className="px-4 pb-6 space-y-4">
      {selectedLocation && (
        <>
          <div>
            <h3 className="text-sm font-bold text-camp-primary flex items-center gap-1.5 mb-2">
              <Location className="w-4 h-4" />
              You Are Here
            </h3>
            <LocationButton
              location={sortedLocationPages[0]}
              handleClick={handleClick}
              selected
            />
          </div>
          <hr className="border-gray-100" />
        </>
      )}
      <div>
        {selectedLocation && (
          <h3 className="text-sm font-bold text-camp-primary mb-2">
            Other Locations
          </h3>
        )}
        <div className="grid grid-cols-2 gap-3">
          {otherLocations.map(locationPage => (
            <LocationButton
              key={locationPage.sys.id}
              location={locationPage}
              handleClick={handleClick}
              selected={false}
            />
          ))}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile: bottom sheet */}
      {disableDialog ? (
        /* Plain overlay — safe to use inside an existing Dialog */
        <Transition show={isOpen} as={Fragment}>
          <div className="md:hidden">
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 z-80 bg-black/40"
                aria-hidden="true"
                onClick={closePopover}
              />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="transition ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Popover.Panel
                ref={mobileScrollRef}
                className="fixed bottom-0 inset-x-0 z-max bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
              >
                <div className="w-9 h-1 bg-gray-200 rounded-full mx-auto mt-3 mb-4" />
                <div className="relative">
                  <h2 className="text-lg font-bold text-camp-primary text-center px-4 mb-4">
                    Choose your location
                  </h2>
                  <button onClick={closePopover}>
                    <Close className="w-4 h-4 absolute right-8 top-0 cursor-pointer" />
                  </button>
                </div>
                <hr className="border-gray-100 mb-4" />
                {sheetContent}
              </Popover.Panel>
            </Transition.Child>
          </div>
        </Transition>
      ) : (
        /* Standard Dialog bottom sheet */
        <Transition show={isOpen} as={Fragment}>
          <Dialog onClose={closePopover} className="md:hidden z-max">
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 z-80 bg-black/40"
                aria-hidden="true"
              />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="transition ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel
                ref={mobileScrollRef}
                className="fixed bottom-0 inset-x-0 z-max bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
              >
                <div className="w-9 h-1 bg-gray-200 rounded-full mx-auto mt-3 mb-4" />
                <div className="relative">
                  <Dialog.Title className="text-lg font-bold text-camp-primary text-center px-4 mb-4">
                    Choose your location
                  </Dialog.Title>
                  <button onClick={closePopover}>
                    <Close className="w-4 h-4 absolute right-8 top-0 cursor-pointer" />
                  </button>
                </div>
                <hr className="border-gray-100 mb-4" />
                {sheetContent}
              </Dialog.Panel>
            </Transition.Child>
          </Dialog>
        </Transition>
      )}

      {/* Desktop: popover panel matching mobile bottom sheet style */}
      <div
        ref={desktopScrollRef}
        className="hidden md:block w-[32rem] px-4 pb-6 max-h-[46rem] overflow-y-auto relative"
      >
        <h2 className="text-lg font-bold text-camp-primary text-center pt-5">
          Choose your location
        </h2>
        <button onClick={closePopover}>
          <Close className="w-4 h-4 absolute right-8 top-5 cursor-pointer" />
        </button>
        <hr className="border-gray-100 mb-4" />

        <div className="space-y-4">
          {selectedLocation && (
            <>
              <div>
                <h3 className="text-sm font-bold text-camp-primary flex items-center gap-1.5 mb-2">
                  <Location className="w-4 h-4" />
                  You Are Here
                </h3>
                <LocationButton
                  location={sortedLocationPages[0]}
                  handleClick={handleClick}
                  selected
                />
              </div>
              <hr className="border-gray-100" />
            </>
          )}

          <div>
            {selectedLocation && (
              <h3 className="text-sm font-bold text-camp-primary mb-2">
                Other Locations
              </h3>
            )}
            <div className="grid grid-cols-3 gap-3">
              {otherLocations.map(locationPage => (
                <LocationButton
                  key={locationPage.sys.id}
                  location={locationPage}
                  handleClick={handleClick}
                  selected={false}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default LocationSelectPopover
